// Ad-click attribution (gclid/UTMs) handed over from the marketing site or
// landed directly on the app. Persisted in localStorage so it survives the
// OAuth redirect dance, then attached to product events and the Stripe checkout
// so purchases stay attributable to the acquiring campaign.
const ATTRIBUTION_KEY = "1t_attribution";

const FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gad_source",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  // The app experience the link opened (e.g. `upload`). Kept with the click so
  // later steps — checkout, payment, app install — stay tied to that flow.
  "entry",
  "angle",
] as const;

const _cookie = (name: string): string | undefined =>
  document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);

export const getStoredAttribution = (): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const captureAttribution = (
  options: { includeMeta?: boolean } = {},
): Record<string, string> => {
  const stored = getStoredAttribution();

  try {
    const params = new URLSearchParams(window.location.search);
    let changed = false;

    for (const field of FIELDS) {
      const value = params.get(field);
      if (value) {
        stored[field] = value;
        changed = true;
      }
    }

    if (options.includeMeta) {
      for (const [field, cookie] of [
        ["fbp", "_fbp"],
        ["fbc", "_fbc"],
      ] as const) {
        const value = _cookie(cookie);
        if (value && stored[field] !== value) {
          stored[field] = value;
          changed = true;
        }
      }
      // The pixel only writes _fbc once it has loaded on the ad-click URL, so
      // purchases often reached the server without a click id. Meta documents
      // building it from fbclid; do that whenever the cookie is absent and the
      // stored value is not already for the latest click.
      if (
        !_cookie("_fbc") &&
        stored.fbclid &&
        !stored.fbc?.endsWith(`.${stored.fbclid}`)
      ) {
        stored.fbc = `fb.1.${Date.now()}.${stored.fbclid}`;
        changed = true;
      }
      stored.source_url = `${window.location.origin}${window.location.pathname}`;
      changed = true;
    } else {
      for (const field of ["fbp", "fbc", "source_url"]) {
        if (field in stored) {
          delete stored[field];
          changed = true;
        }
      }
    }

    if (!stored.referrer && document.referrer) {
      stored.referrer = document.referrer;
      changed = true;
    }

    if (changed) {
      window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(stored));
    }
  } catch {}

  return stored;
};

const META_SOURCES = new Set(["meta", "facebook", "fb", "instagram", "ig"]);

/**
 * Which paid or referral channel a visit came from. Click ids win over UTMs
 * because ad platforms add them automatically (Google auto-tagging often sends
 * only `gclid`), while UTMs are hand-typed labels.
 */
export const getAttributionChannel = (
  attribution: Record<string, string | undefined>,
): string => {
  const source = attribution.utm_source?.trim().toLowerCase();
  if (attribution.fbclid || (source && META_SOURCES.has(source))) return "meta";
  if (
    attribution.gclid ||
    attribution.gbraid ||
    attribution.wbraid ||
    attribution.gad_source ||
    source === "google"
  ) {
    return "google";
  }
  return source || "direct";
};
